import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { getPostBySlug } from "@/data/blogData";
import useSeo from "@/lib/useSeo";
import { blogPostingSchema, breadcrumbSchema } from "@/lib/schema";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useSeo({
    title: post ? (post.seo_title || `${post.title} | ClariDent RCM`) : "Article not found | ClariDent RCM",
    description: post ? (post.seo_description || post.excerpt) : "The article you are looking for could not be found.",
    path: post ? `/resources/${post.slug}` : undefined,
    image: post ? `https://claridentrcm.com${post.cover}` : undefined,
    robots: post ? "index, follow" : "noindex, follow",
    schemas: post
      ? [
          blogPostingSchema(post),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Resources", path: "/resources" },
            { name: post.title, path: `/resources/${post.slug}` },
          ]),
        ]
      : [],
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <main className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="blog-not-found">
        <h1 className="font-display text-4xl font-bold text-navy-900">Article not found</h1>
        <Link to="/resources" className="mt-6 inline-block text-teal-600 font-bold text-sm">Back to Resources</Link>
      </main>
    );
  }

  return (
    <main data-testid="blog-post-page">
      <article className="max-w-3xl mx-auto px-6 py-20">
        <Link to="/resources" className="inline-flex items-center gap-2 text-sm font-bold text-teal-600 mb-10 hover:gap-3 transition-all" data-testid="blog-back-link">
          <ArrowLeft size={16} /> All articles
        </Link>
        <div className="flex items-center gap-3 text-xs font-bold uppercase tracking-widest mb-5">
          <span className="text-teal-600">{post.category}</span>
          <span className="text-slate-400">{post.read_time}</span>
          <span className="text-slate-400">{post.published_at}</span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-bold text-navy-900 leading-tight" data-testid="blog-post-title">{post.title}</h1>
        <img src={post.cover} alt={post.title} className="rounded-3xl w-full h-72 object-cover my-12" />
        <div className="space-y-6">
          {post.content.map((block, i) => {
            if (block.type === "h2") {
              return <h2 key={i} className="font-display text-2xl sm:text-3xl font-semibold text-navy-900 pt-6">{block.text}</h2>;
            }
            if (block.type === "list") {
              return (
                <ul key={i} className="space-y-3 list-disc pl-6 text-slate-600 text-base leading-relaxed">
                  {block.items.map((item) => <li key={item}>{item}</li>)}
                </ul>
              );
            }
            return <p key={i} className="text-base sm:text-lg text-slate-600 leading-relaxed">{block.text}</p>;
          })}
        </div>
        <div className="mt-16 rounded-3xl bg-navy-950 p-10 text-center relative overflow-hidden" data-testid="blog-cta">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(14,143,143,0.25),transparent_65%)]" />
          <div className="relative">
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-white">Curious what your practice is leaving uncollected?</h3>
            <p className="mt-3 text-slate-400 text-sm">Get a free RCM audit — claims, denials, and AR reviewed at no cost.</p>
            <Link to="/book" data-testid="blog-cta-btn" className="mt-7 inline-flex items-center gap-2 rounded-full bg-teal-600 px-8 py-3.5 text-sm font-bold text-white transition-all duration-200 hover:bg-teal-500 hover:-translate-y-1">
              Get Your Free RCM Audit <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </article>
    </main>
  );
}
